import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecommendationCard = ({ recommendation, rank, onViewROI, isGenerating }) => {
  const getCategoryColor = (category) => {
    const colors = {
      'Governance': 'bg-purple-50 text-purple-700 border-purple-200',
      'Analytics': 'bg-blue-50 text-blue-700 border-blue-200',
      'Infrastructure': 'bg-teal-50 text-teal-700 border-teal-200',
      'Strategy': 'bg-[#E6F0F5] text-[#002B45] border-[#002B45]/20',
      'Skills': 'bg-orange-50 text-orange-700 border-orange-200',
      'Data Management': 'bg-green-50 text-green-700 border-green-200',
      'Technology': 'bg-[#D1F5F0] text-[#1FB5A5] border-[#1FB5A5]/30'
    };
    return colors?.[category] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const getROIColor = (roi) => {
    if (roi >= 200) return 'text-green-600';
    if (roi >= 100) return 'text-[#1FB5A5]';
    if (roi >= 50) return 'text-blue-600';
    return 'text-gray-600';
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'bg-green-500';
    if (confidence >= 60) return 'bg-[#1FB5A5]';
    if (confidence >= 40) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  return (
    <div className="bg-white rounded-xl shadow-elevation-1 hover:shadow-elevation-2 transition-shadow duration-300 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#002B45] to-[#003D5C] p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#1FB5A5] rounded-full flex items-center justify-center text-white font-bold">
              #{rank}
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">{recommendation?.serviceName}</h3>
              <span className={`inline-block px-2 py-1 rounded text-xs font-medium border mt-1 ${getCategoryColor(recommendation?.category)}`}>
                {recommendation?.category}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Rationale */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="FileText" size={16} className="text-[#002B45]" />
            <h4 className="font-semibold text-[#002B45]">AI Rationale</h4>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">{recommendation?.rationale}</p>
        </div>

        {/* ROI Metrics */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
            <p className="text-xs text-green-700 mb-1 font-medium">Predicted ROI</p>
            <div className={`text-3xl font-bold ${getROIColor(recommendation?.predictedROI)}`}>
              {recommendation?.predictedROI}%
            </div>
            <div className="mt-2">
              <div className="flex items-center justify-between text-xs text-green-700 mb-1">
                <span>Confidence</span>
                <span>{recommendation?.roiConfidence}%</span>
              </div>
              <div className="w-full bg-green-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${getConfidenceColor(recommendation?.roiConfidence)}`}
                  style={{ width: `${recommendation?.roiConfidence}%` }}
                />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
            <p className="text-xs text-blue-700 mb-1 font-medium">Implementation</p>
            <div className="text-3xl font-bold text-blue-600">
              {recommendation?.implementationMonths}
            </div>
            <p className="text-xs text-blue-700 mt-1">months timeline</p>
            <div className="mt-2">
              <p className="text-xs text-blue-700 font-medium">Cost Range</p>
              <p className="text-sm text-blue-600 font-semibold">{recommendation?.costRange}</p>
            </div>
          </div>
        </div>

        {/* Success Metrics */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Target" size={16} className="text-[#1FB5A5]" />
            <h4 className="font-semibold text-[#002B45]">Key Success Metrics</h4>
          </div>
          <div className="space-y-2">
            {recommendation?.successMetrics?.map((metric, idx) => (
              <div key={idx} className="flex items-start gap-2 bg-gray-50 rounded-lg p-3">
                <Icon name="CheckCircle" size={14} className="text-[#1FB5A5] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">{metric}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Risk Factors */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="AlertTriangle" size={16} className="text-orange-600" />
            <h4 className="font-semibold text-[#002B45]">Risk Factors</h4>
          </div>
          <div className="space-y-2">
            {recommendation?.riskFactors?.map((risk, idx) => (
              <div key={idx} className="flex items-start gap-2 bg-orange-50 rounded-lg p-3">
                <Icon name="AlertCircle" size={14} className="text-orange-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-orange-700">{risk}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Action Button */}
        <Button
          onClick={onViewROI}
          disabled={isGenerating}
          fullWidth
          iconName={isGenerating ? 'Loader2' : 'TrendingUp'}
          iconPosition="left"
          className="bg-[#1FB5A5] hover:bg-[#1FB5A5]/90"
        >
          {isGenerating ? 'Generating Scenarios...' : 'View ROI Scenarios'}
        </Button>
      </div>
    </div>
  );
};

export default RecommendationCard;